import { Facet, Suggestion } from '@empathy/search-types';
import { EmpathizeState, EmpathyXConfig, PopularSearchesConfig } from '../../../models';
import { SuggestionsConfig } from '../../../models/config/components/suggestions-config.model';
import { normalizeString } from '../../../utils';
import { GETTERS_KEYS } from '../../store.const';
import { RootStoreState } from '../../store.interfaces';
import { EMPATHIZE_GETTERS_KEYS } from '../empathize.const';
import { EmpathizeGetter } from '../empathize.interfaces';

export class SuggestionsGetter implements EmpathizeGetter {
  protected rootState: RootStoreState;
  getters = {
    [EMPATHIZE_GETTERS_KEYS.rawTermSuggestions]: this.createRawSuggestionsGetter('termSuggestions'),
    [EMPATHIZE_GETTERS_KEYS.rawPopularSearches]: this.createRawSuggestionsGetter('popularSearches'),
    [EMPATHIZE_GETTERS_KEYS.termSuggestions]: this.createSuggestionsGetter('termSuggestions', EMPATHIZE_GETTERS_KEYS.rawTermSuggestions),
    [EMPATHIZE_GETTERS_KEYS.popularSearches]: this.createSuggestionsGetter('popularSearches', EMPATHIZE_GETTERS_KEYS.rawPopularSearches)
  };

  protected createRawSuggestionsGetter(type: 'popularSearches' | 'termSuggestions') {
    return (localState: EmpathizeState, getters: any, rootState: RootStoreState, rootGetters: any): Suggestion[] => {
      this.rootState = rootState;
      const config: EmpathyXConfig = rootGetters[GETTERS_KEYS.config.config];
      return this.generateSuggestions(localState[type], config[type]);
    };
  }

  protected createSuggestionsGetter(type: 'popularSearches' | 'termSuggestions', rawGetterKey: keyof typeof EMPATHIZE_GETTERS_KEYS) {
    return (localState: EmpathizeState, getters: any, rootState: RootStoreState, rootGetters: any): Suggestion[] => {
      this.rootState = rootState;
      const config: EmpathyXConfig = rootGetters[GETTERS_KEYS.config.config];
      let suggestions: Suggestion[] = getters[EMPATHIZE_GETTERS_KEYS[rawGetterKey]];
      if (type === 'popularSearches') {
        suggestions = this.siftPopularWithHistoryQueries(suggestions, config[type]);
      }
      return this.filterSuggestions(suggestions, config[type]);
    };
  }

  protected generateSuggestions(suggestions: Suggestion[], config: SuggestionsConfig): Suggestion[] {
    return suggestions.reduce<Suggestion[]>((generatedSuggestions, suggestion) => {
      if (suggestion.facets.length) {
        generatedSuggestions.push(...this.generateSuggestionsWithFacets(suggestion, config));
      } else {
        generatedSuggestions.push(suggestion);
      }
      return generatedSuggestions;
    }, []);
  }

  protected generateSuggestionsWithFacets(suggestion: Suggestion, config: SuggestionsConfig): Suggestion[] {
    const initialSuggestions = config.showExtraSuggestionWithoutFacet ? [{ ...suggestion, facets: [] as Facet[] }] : [];
    return suggestion.facets.reduce<Suggestion[]>((facetsSuggestions, facet) =>
        this.generateSuggestionsForFacet(suggestion, facet, facetsSuggestions)
      , initialSuggestions);
  }

  protected generateSuggestionsForFacet(suggestion: Suggestion, facet: Facet, initialSuggestions: Suggestion[]): Suggestion[] {
    return facet.filters.reduce<Suggestion[]>((filterSuggestions, filter) => {
      const facetCopy = { ...facet };
      facetCopy.filters = [{ ...filter, facet: facetCopy, selected: true }];
      filterSuggestions.push({ ...suggestion, facets: [facetCopy], key: `${ suggestion.term }-in-${ filter.id }` });
      return filterSuggestions;
    }, initialSuggestions);
  }

  protected filterSuggestions(suggestions: Suggestion[], config: SuggestionsConfig): Suggestion[] {
    const filteredSuggestions = config.showSameQuerySuggestion
      ? suggestions
      : this.filterEqualSuggestionsQuery(suggestions, this.rootState.core.inputQuery);
    return filteredSuggestions.slice(0, config.size);
  }

  protected filterEqualSuggestionsQuery(suggestions: Suggestion[], query: string): Suggestion[] {
    const normalizedQuery = normalizeString(query);
    return suggestions.filter(suggestion => normalizeString(suggestion.term) !== normalizedQuery || suggestion.facets.length);
  }

  protected siftPopularWithHistoryQueries(popularSearches: Suggestion[], config: PopularSearchesConfig): Suggestion[] {
    const historyQueries = this.rootState.history.queryHistory;
    const filteredPopularSearches = popularSearches.filter(
      popularSearch => historyQueries.every(historyQuery => popularSearch.term !== historyQuery.term));
    return config.siftWithHistoryQueries ? filteredPopularSearches : popularSearches;
  }
}
